import { useEffect, useRef, useState } from "react";
import { Page } from "../components/ui.jsx";
import ShipIcon from "../components/ShipIcon.jsx";
import SERIES from "../data/series/ship-happens.json";
import { buildGuide, deliver } from "../lib/shipGuide.js";

// The Ship Happens series — a field guide to the ten ships.
//
// Everything on this page renders from the JSON. There is no schedule, no
// "this week", no ship marked taught or untaught, and no date logic anywhere
// in this file: the teaching calendar is decided elsewhere and the page is
// the whole guide from the day it ships. If that ever needs to change it is a
// separate build, not a flag here.

const JOURNAL_KEY = (shipId, i) => `shipcheck:${shipId}:${i}`;

const readAnswer = (shipId, i) => {
  try {
    return localStorage.getItem(JOURNAL_KEY(shipId, i)) || "";
  } catch {
    return "";
  }
};

const writeAnswer = (shipId, i, text) => {
  try {
    if (text.trim()) localStorage.setItem(JOURNAL_KEY(shipId, i), text);
    else localStorage.removeItem(JOURNAL_KEY(shipId, i));
  } catch {
    /* private mode — the answer lives for the session and no longer */
  }
};

/**
 * Whether the browser should be handed the promo at all.
 *
 * The clip is muted decoration, and five megabytes of decoration is the wrong
 * trade for somebody who has asked for less motion or is on a metered
 * connection. They get the poster, which is the same frame the video opens on.
 */
const wantsVideo = () => {
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    if (navigator.connection && navigator.connection.saveData) return false;
  } catch {
    /* an old browser; fall through and let it try */
  }
  return true;
};

/** The hero — promo on top, title lockup and the one primary action beneath. */
function Hero({ onShare, shared }) {
  const [video] = useState(wantsVideo);
  const { assets, title, subtitle, source } = SERIES;

  // The title has the period picked out in the accent, the way the cover does.
  // That is the single accent element in this viewport.
  const lockup = title.endsWith(".") ? title.slice(0, -1) : title;

  return (
    <div className="sh-hero">
      <div className="sh-film">
        {video ? (
          <video
            className="sh-video"
            src={assets.promo}
            poster={assets.promoPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          />
        ) : (
          <img className="sh-video" src={assets.promoPoster} alt="" aria-hidden="true" />
        )}
        <div className="sh-filmfade" />
      </div>

      <div className="sh-lockup">
        <div className="sh-eyebrow">From the book by {source.author}</div>
        <h1 className="sh-title">
          {lockup}
          <span className="sh-dot">.</span>
        </h1>
        <p className="sh-sub">{subtitle}</p>
        <button className="sh-cta" onClick={onShare}>
          {shared ? "✓ Link copied" : "Invite somebody"}
        </button>
      </div>
    </div>
  );
}

/** One prompt and its answer. Saves on a pause in typing and again on blur. */
function Prompt({ shipId, index, text, onSaved }) {
  const [value, setValue] = useState(() => readAnswer(shipId, index));
  const timer = useRef(null);

  const save = (v) => {
    writeAnswer(shipId, index, v);
    onSaved();
  };

  const onChange = (e) => {
    const v = e.target.value;
    setValue(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(v), 500);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <label className="sh-prompt">
      <span className="sh-promptq">{text}</span>
      <textarea
        className="sh-answer"
        value={value}
        onChange={onChange}
        onBlur={() => {
          clearTimeout(timer.current);
          save(value);
        }}
        placeholder="Write it down."
        rows={2}
      />
    </label>
  );
}

/**
 * One ship. Collapsed: number, name, vessel, the question. Expanded: the lot.
 *
 * Each card owns its own open state on purpose. People compare ships side by
 * side, so opening one must never close another.
 */
function Ship({ ship, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  // Whether any prompt on this ship has a saved answer — the only feedback
  // the journal gives, by design. No streaks, no percentages.
  const [answered, setAnswered] = useState(() =>
    ship.shipCheck.some((_, i) => readAnswer(ship.id, i)),
  );
  const refresh = () => setAnswered(ship.shipCheck.some((_, i) => readAnswer(ship.id, i)));

  const n = String(ship.order).padStart(2, "0");
  const hasMedia = ship.media && (ship.media.youtubeId || ship.media.audioUrl || ship.media.notesUrl);

  return (
    <article className={"sh-ship" + (open ? " open" : "")} id={ship.id}>
      <button
        className="sh-shiphead"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={ship.id + "-body"}
      >
        <span className="sh-num" aria-hidden="true">
          {n}
        </span>
        <span className="sh-headcopy">
          <span className="sh-shipname">
            {ship.name}
            {answered && <span className="sh-mark" title="You have written on this ship" />}
          </span>
          <span className="sh-vessel">{ship.vessel}</span>
          <span className="sh-q">{ship.question}</span>
        </span>
        <span className="sh-icon">
          <ShipIcon id={ship.id} size={42} />
        </span>
      </button>

      {open && (
        <div className="sh-shipbody" id={ship.id + "-body"}>
          <div className="sh-field">
            <span className="sh-lab">Built for</span>
            <p>{ship.builtFor}</p>
          </div>
          <div className="sh-field">
            <span className="sh-lab">The lesson</span>
            <p>{ship.lesson}</p>
          </div>
          <div className="sh-field">
            <span className="sh-lab">The value</span>
            <p>{ship.value}</p>
          </div>
          <div className="sh-field">
            <span className="sh-lab">The danger</span>
            <p>{ship.danger}</p>
          </div>

          <blockquote className="sh-quote">{ship.pullQuote}</blockquote>

          <div className="sh-field">
            <span className="sh-lab">Anchor</span>
            <p className="sh-scripture">{ship.scripture.map((s) => s.ref).join(" · ")}</p>
          </div>

          {/* Media only appears once something exists to show. A dead "Watch"
              control would also leak the schedule the page refuses to carry. */}
          {hasMedia && (
            <div className="sh-media">
              {ship.media.youtubeId && (
                <a
                  className="sh-medialink"
                  href={`https://www.youtube.com/watch?v=${ship.media.youtubeId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  ▶ Watch
                </a>
              )}
              {ship.media.audioUrl && (
                <a className="sh-medialink" href={ship.media.audioUrl} target="_blank" rel="noreferrer">
                  🎧 Listen
                </a>
              )}
              {ship.media.notesUrl && (
                <a className="sh-medialink" href={ship.media.notesUrl} target="_blank" rel="noreferrer">
                  📝 Notes
                </a>
              )}
            </div>
          )}

          <div className="sh-check">
            <div className="sh-checkhead">
              <span className="sh-lab">Ship check</span>
            </div>
            {ship.shipCheck.map((q, i) => (
              <Prompt key={i} shipId={ship.id} index={i} text={q} onSaved={refresh} />
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

/** Every answer on the device, keyed by ship, one slot per prompt. */
const allAnswers = () =>
  Object.fromEntries(
    SERIES.fleet.map((ship) => [ship.id, ship.shipCheck.map((_, i) => readAnswer(ship.id, i))]),
  );

export default function ShipHappens({ back }) {
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exported, setExported] = useState("");
  const { premise, closing, book, journal, assets, source, serviceTimes, location } = SERIES;

  const share = async () => {
    const url = window.location.origin + "/ship-happens";
    const text = `${SERIES.title} — ${SERIES.subtitle}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: SERIES.title, text, url });
        return;
      }
    } catch {
      /* dismissed the sheet, or it failed — fall through to copying */
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2200);
    } catch {
      /* no clipboard either; the address bar still has it */
    }
  };

  /**
   * The study guide as a file to keep: the whole field guide with the reader's
   * own answers set into it. Built on the device, from the device. It is worth
   * saving even with nothing written — the blank version is a workbook.
   */
  const saveGuide = async () => {
    if (saving) return;
    setSaving(true);
    setExported("");
    try {
      const doc = await buildGuide(SERIES, allAnswers());
      const outcome = await deliver(doc, journal.filename);
      if (outcome === "saved") setExported("Saved. Check your downloads.");
      else if (outcome === "shared") setExported("Sent to where you chose.");
    } catch {
      setExported("Could not build the file just now. Try again in a moment.");
    }
    setSaving(false);
    setTimeout(() => setExported(""), 3200);
  };

  return (
    <Page back={back} tone="sh" title={null}>
      <Hero onShare={share} shared={shared} />

      {/* ---- premise ---- */}
      <section className="sh-premise">
        {premise.map((p, i) => (
          <p key={i} className={i === premise.length - 1 ? "sh-premise-last" : ""}>
            {p}
          </p>
        ))}
      </section>

      {/* ---- the fleet ---- */}
      <section className="sh-fleet">
        <div className="sh-secthead">
          <div className="sh-eyebrow">The fleet</div>
          <h2 className="sh-h2">Ten ships.</h2>
          <p className="sh-lede">
            Each one built for different water. Open any of them — they all stay open
            as long as you want them.
          </p>
        </div>

        <div className="sh-privacy">{journal.privacyNote}</div>

        {SERIES.fleet.map((ship, i) => (
          <Ship key={ship.id} ship={ship} defaultOpen={i === 0} />
        ))}

        <div className="sh-export">
          <button className="sh-cta ink" onClick={saveGuide} disabled={saving}>
            {saving ? "Building…" : "Save my study guide (PDF)"}
          </button>
          <span className="sh-exporthint">
            The full field guide with your Ship Check answers in it — yours to keep.
          </span>
          {exported && <span className="sh-exportnote">{exported}</span>}
        </div>
      </section>

      {/* ---- closing ---- */}
      <section className="sh-closing">
        {closing.map((p, i) => (
          <p key={i} className={i === closing.length - 1 ? "sh-closing-last" : ""}>
            {p}
          </p>
        ))}
      </section>

      {/* ---- the book ---- */}
      <section className="sh-book">
        <img
          className="sh-cover"
          src={assets.coverSmall}
          srcSet={`${assets.coverSmall} 480w, ${assets.cover} 1200w`}
          sizes="(max-width: 640px) 40vw, 220px"
          alt={`${source.title} by ${source.author} — front cover`}
        />
        <div className="sh-bookcopy">
          <div className="sh-eyebrow">The book</div>
          <h2 className="sh-h2">{source.title}.</h2>
          <p className="sh-by">
            {source.author} · {source.year}
          </p>
          <p className="sh-blurb">{book.blurb}</p>
          {book.status && <p className="sh-soon">{book.status}</p>}
          {/* No control at all until there is somewhere to send people. */}
          {book.purchaseUrl && (
            <a className="sh-cta" href={book.purchaseUrl} target="_blank" rel="noreferrer">
              {book.purchaseLabel || "Get the book"}
            </a>
          )}
          {book.inPerson && <p className="sh-inperson">{book.inPerson}</p>}
        </div>
      </section>

      <footer className="sh-foot">
        <span>{serviceTimes.join(" · ")}</span>
        <span>{location}</span>
      </footer>
    </Page>
  );
}
